import i18n from '@/i18n';
import { lingtuImageAPI, stampExtractAPI } from '@/utils/api';
import { sanitizeIpeUserMessage, toIpeUserErrorMessage } from './errorHandler';
import { logIpeClientError } from '@/utils/ipeClientErrorLogger';
import { resolveImg2ImgReferenceForApi } from './img2imgPublicUrl';
import { pollLingtuJob } from '@/image-crop';
import { submitAndPollImageEnhance } from './imageEnhanceServerProcess';
import {
  normalizeSmartCutoutFormats,
} from '@/pages/ipe/workbench/constants/imageProcessStudioConfig';

function buildCutoutRequestId(batchNo, imageId) {
  const batch = String(batchNo || '').replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 40);
  const iid = String(imageId || '').replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 40);
  return `cutout_${batch}_${iid}`;
}

function buildStampRequestId(batchNo, imageId, retryToken = '') {
  const batch = String(batchNo || '').replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 40);
  const iid = String(imageId || '').replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 40);
  const retry = retryToken
    ? `_r${String(retryToken).replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 16)}`
    : '';
  return `stamp_${batch}_${iid}${retry}`;
}

export function extractStampExtractResultUrl(res) {
  if (!res) return '';
  if (typeof res === 'string' && res.trim()) return res.trim();
  const direct = res?.url || res?.imageUrl || res?.outputUrl;
  if (direct && String(direct).trim()) return String(direct).trim();
  const urls = res?.urls;
  if (Array.isArray(urls) && urls[0]) return String(urls[0]).trim();
  const data = res?.data;
  if (typeof data === 'string' && data.trim()) return data.trim();
  if (data && typeof data === 'object') {
    const fromData = data.url || data.imageUrl || data.outputUrl;
    if (fromData && String(fromData).trim()) return String(fromData).trim();
    if (Array.isArray(data.urls) && data.urls[0]) return String(data.urls[0]).trim();
  }
  return '';
}

export function buildImageProcessBatchFailureMessage(outputs, actionLabel = i18n.t('workbench:ipeUtilError.genericImage')) {
  const failed = (outputs || []).filter((row) => row?.status === 'failed');
  if (failed.length === 0) {
    return i18n.t('workbench:ipeUtilError.batchNoOutput', { action: actionLabel });
  }
  const reasons = failed
    .map((row, idx) => {
      const n = Number(row.sourceIndex ?? row.index ?? idx) + 1;
      return i18n.t('workbench:ipeUtilError.batchRowFailed', { index: n, message: sanitizeIpeUserMessage(row.error, i18n.t('workbench:ipeUtilError.processFailed')) });
    })
    .join(i18n.language?.startsWith('zh') ? '；' : '; ');
  return i18n.t('workbench:ipeUtilError.batchFailed', { action: actionLabel, reasons });
}

export function buildImageProcessRetryFailureMessage(outputs, actionLabel = i18n.t('workbench:ipeUtilError.genericImage')) {
  const failed = (outputs || []).filter((row) => row?.status === 'failed');
  if (failed.length === 0) {
    return i18n.t('workbench:ipeUtilError.retryNoOutput', { action: actionLabel });
  }
  const reasons = failed
    .map((row, idx) => {
      const n = Number(row.sourceIndex ?? row.index ?? idx) + 1;
      return i18n.t('workbench:ipeUtilError.batchRowFailed', { index: n, message: sanitizeIpeUserMessage(row.error, i18n.t('workbench:ipeUtilError.processFailed')) });
    })
    .join(i18n.language?.startsWith('zh') ? '；' : '; ');
  return i18n.t('workbench:ipeUtilError.retryFailed', { reasons });
}

function buildUpscaleRequestId(batchNo, imageId) {
  const batch = String(batchNo || '').replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 40);
  const iid = String(imageId || '').replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 40);
  return `upscale_${batch}_${iid}`;
}

function extractJobId(submitResponse, fallbackRequestId) {
  if (!submitResponse) return fallbackRequestId;
  if (typeof submitResponse === 'string' && submitResponse.trim()) {
    return submitResponse.trim();
  }
  const jobId = submitResponse.jobId || submitResponse.taskId || submitResponse.id;
  if (jobId) return String(jobId).trim();
  const data = submitResponse.data;
  if (typeof data === 'string' && data.trim()) return data.trim();
  if (data && typeof data === 'object' && data.jobId) return String(data.jobId).trim();
  return fallbackRequestId;
}

async function submitAndPollLingtu(submitFn, imageUrl, requestId) {
  const resolved = await resolveImg2ImgReferenceForApi(imageUrl);
  if (!resolved) throw new Error(i18n.t('workbench:ipeUtilError.imageUrlUnresolved'));
  const submit = await submitFn(resolved, requestId);
  const jobId = extractJobId(submit, requestId);
  const poll = await pollLingtuJob(jobId);
  if (!poll.ok) {
    throw new Error(poll.message || i18n.t('workbench:ipeUtilError.processFailed'));
  }
  return { url: poll.url, jobId, requestId, rawResult: poll.rawResult };
}

async function processUpscaleItem(subAccount, item, imageUrl, enhanceOptions = {}) {
  const requestId = buildUpscaleRequestId(subAccount, item.id);
  const resolved = await resolveImg2ImgReferenceForApi(imageUrl);
  if (!resolved) throw new Error(i18n.t('workbench:ipeUtilError.imageUrlUnresolved'));
  return submitAndPollImageEnhance({
    requestId,
    imageUrl: resolved,
  });
}

async function processCutoutItem(subAccount, item, imageUrl, cutoutOptions = {}) {
  const requestId = buildCutoutRequestId(subAccount, item.id);
  const formats = normalizeSmartCutoutFormats(cutoutOptions.formats);
  const mode = String(cutoutOptions.mode || '').trim().toLowerCase() === 'advanced' ? 'advanced' : 'basic';
  const polled = await submitAndPollLingtu(
    (url, reqId) => lingtuImageAPI.cutoutSubmit({
      imageUrl: url,
      requestId: reqId,
      formats,
      mode,
    }),
    imageUrl,
    requestId,
  );
  return {
    ...polled,
    engine: polled.rawResult?.engine || (mode === 'advanced' ? 'qwen-image-2.0+bria-rmbg' : 'aliyun-imageseg'),
  };
}

/** 工作流 / 重试等场景复用智能抠图处理 */
export async function runSmartCutoutProcess(subAccount, item, imageUrl, cutoutOptions = {}, options = {}) {
  return processCutoutItem(subAccount, item, imageUrl, cutoutOptions, options);
}

async function processStampItem(subAccount, item, imageUrl, stampOptions = {}, { retryToken } = {}) {
  const requestId = buildStampRequestId(subAccount, item.id, retryToken);
  const resolved = await resolveImg2ImgReferenceForApi(imageUrl);
  if (!resolved) throw new Error(i18n.t('workbench:ipeUtilError.imageUrlUnresolved'));

  const prompt = String(stampOptions.prompt || '').trim();
  const res = await stampExtractAPI.submitAndWait({
    imageUrl: resolved,
    requestId,
    ...(prompt ? { prompt } : {}),
  });
  const url = extractStampExtractResultUrl(res);
  if (!url) {
    throw new Error(i18n.t('workbench:ipeUtilError.stampExtractNoImage'));
  }
  return {
    url,
    jobId: res?.jobId || res?.requestId || requestId,
    requestId,
    rawResult: res,
    engine: res?.engine || 'qwen-image-2.0',
    prompt: prompt || res?.prompt || '',
    userPrompt: prompt,
  };
}

/**
 * @param {string} navId
 * @param {{ taskId: string, subAccount: string, sources: object[], imageUrls: string[], cutoutOptions?: object, enhanceOptions?: object, stampOptions?: object, onProgress?: Function, retryToken?: string }} params
 */
export async function runImageProcessStudioBatch(navId, {
  taskId,
  subAccount,
  sources,
  imageUrls,
  cutoutOptions,
  enhanceOptions,
  stampOptions,
  onProgress,
  retryToken,
}) {
  const collectedUrls = [];
  const outputs = [];
  let failedCount = 0;

  const emitProgress = async () => {
    if (typeof onProgress !== 'function') return;
    await onProgress({
      successCount: collectedUrls.length,
      failedCount,
      outputs: [...outputs],
    });
  };

  for (let i = 0; i < sources.length; i += 1) {
    const item = sources[i];
    const imageUrl = imageUrls[i];
    const sourceIndex = Number.isFinite(Number(item.sourceIndex)) ? Number(item.sourceIndex) : i;
    try {
      let result;
      if (navId === 'imageEnhance') {
        result = await processUpscaleItem(subAccount, item, imageUrl, enhanceOptions);
      } else if (navId === 'smartCutout') {
        result = await processCutoutItem(subAccount, item, imageUrl, cutoutOptions);
      } else if (navId === 'stampExtract') {
        result = await processStampItem(subAccount, item, imageUrl, stampOptions, { retryToken });
      } else {
        throw new Error(i18n.t('workbench:ipeUtilError.unknownTool', { nav: navId }));
      }
      collectedUrls.push(result.url);
      outputs.push({
        url: result.url,
        outputUrl: result.url,
        imageUrl: result.url,
        status: 'success',
        index: sourceIndex,
        sourceIndex,
        sourceImageId: item.id,
        jobId: result.jobId || undefined,
        ...(result.engine ? { engine: result.engine } : {}),
        ...(result.prompt ? { prompt: result.prompt } : {}),
        ...(result.userPrompt ? { userPrompt: result.userPrompt } : {}),
      });
    } catch (error) {
      failedCount += 1;
      const userMessage = toIpeUserErrorMessage(error, i18n.t('workbench:ipeUtilError.processFailed'));
      if (!error?.ipeIntercepted) {
        logIpeClientError(`${navId}-batch`, error, userMessage, {
          taskId,
          sourceIndex,
          sourceImageId: item.id,
        });
      }
      outputs.push({
        status: 'failed',
        index: sourceIndex,
        sourceIndex,
        sourceImageId: item.id,
        error: userMessage,
      });
    }
    await emitProgress();
  }

  return { collectedUrls, outputs, failedCount };
}
